/**
 * CorruptionLayer (S126 / BL-112 / D-224) — the ECHELON corruption renderer.
 * Design contract: docs/design/ACT_III_CORRUPTION.md ("HER SIGNAL").
 *
 * Four vocabularies, one entity, one knob (`CorruptionTheme.corruptionIntensity`):
 *  - claimed cells (her hand)   → renderUnder, on the board surface
 *  - the veil (her weather)     → renderOver, Act III only
 *  - the sigil (her face)       → renderOver, "she looks back"
 *  - signal tears (her violence)→ renderOver, Act III + high quality only
 *
 * THE THREE RAILS (docs/prompts/session_126_act3_corruption.md §0.A):
 *  - Render-only. Reads (wave, tone, quality, grid topology) are read-only;
 *    emits nothing, mutates nothing outside itself, never touches `gs.rng` or
 *    sim time. Animates on an ACCUMULATED render clock + a render-local PRNG +
 *    deterministic cell hashes — two layers fed the same clock draw the same
 *    frame (locked by tests/rendering/CorruptionLayer.test.ts).
 *  - Act I pixel-identical: intensity is hard-zero ≤ wave 12 and every method
 *    early-returns before its first draw call at intensity 0.
 *  - Photosensitivity-safe by construction: claim cycles ≥ 1.8 s (one cell
 *    ≈ 0.45 % of frame), sigil alpha ramps ≥ 1.2 s, veil breath period 8 s at
 *    ≤ 0.08 amplitude, tears are small-area DISPLACEMENT (≤ 8 px strips,
 *    ≤ 2 concurrent, ≥ ~0.6 s spacing at peak ⇒ < 3 events/s). No full-frame
 *    luminance step exists in this file. Argument: Art Bible §8.
 */

import type { Grid } from "../core/Grid.ts";
import type { QualityLevel } from "../core/types.ts";
import { CELL_SIZE } from "../core/config.ts";
import { CellType } from "../core/types.ts";
import {
  arenaMotionScale,
  prefersReducedMotionFlash,
} from "../engine/MotionSettings.ts";
import { canvasFont } from "./canvasType.ts";
import { COLORS, ECHELON } from "./designTokens.ts";
import { getOperatorAccent } from "./EpochTheme.ts";
import {
  type CorruptionPressure,
  type CorruptionQualityGates,
  cellHash,
  corruptionIntensity,
  corruptionQualityGates,
  createRenderPrng,
  isDominion,
} from "./CorruptionTheme.ts";

/** Claim cycle — a cell eases in, holds, eases out across this period (s). */
const CLAIM_CYCLE_S = 2.4;
/** Fraction of the cycle spent easing in / out (slow, sub-threshold). */
const CLAIM_EASE = 0.25;
/** Peak average claim probability per cell at intensity 1 (≈ 17–24 cells). */
const CLAIM_P_MAX = 0.1;
/** Hard per-frame draw cap for claimed cells at cellDensity 1. */
const CLAIM_CAP = 24;
/** Sigil lifetime phases (s): form, hold, dissolve. */
const SIGIL_FORM_S = 1.6;
const SIGIL_HOLD_S = 2.8;
const SIGIL_FADE_S = 1.6;
/** Veil breathing period (s) — slow by construction (Bible §8). */
const VEIL_BREATH_S = 8;
/** Max alpha of the veil at intensity 1. */
const VEIL_ALPHA_MAX = 0.22;
/** Tear strip geometry/lifetime bounds. */
const TEAR_MIN_H = 3;
const TEAR_MAX_H = 8;
const TEAR_LIFE_MIN_S = 0.09;
const TEAR_LIFE_MAX_S = 0.15;
/** Concurrent tears are capped (area rail). */
const TEAR_MAX_ACTIVE = 2;

interface Tear {
  y: number;
  h: number;
  dx: number;
  until: number;
}

interface Sigil {
  cx: number;
  cy: number;
  bornAt: number;
  /** 0..1 — how *formed* the iris draws (proto in Act II, full in Act III). */
  formed: number;
  radius: number;
}

function smoothstep(t: number): number {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export class CorruptionLayer {
  private readonly grid: Grid;
  /** Accumulated render seconds — NEVER wall clock (determinism + pause-safe). */
  private clock = 0;
  private intensity_ = 0;
  private dominion = false;
  private quality: QualityLevel = "high";
  private operatorAccent: string = COLORS.SENTINEL_GREEN;
  private readonly prng = createRenderPrng(0x51c6);

  private tears: Tear[] = [];
  private nextTearAt = 0;
  private sigil: Sigil | null = null;
  private nextSigilAt = 0;

  /** Per-cell spread rank 0 (claims first, spawn-side) → 1 (claims last). */
  private spreadRank: Float32Array | null = null;
  private spreadRankAt = -1;

  private veilGrad: CanvasGradient | null = null;
  private veilGradW = 0;
  private veilGradH = 0;

  constructor(grid: Grid) {
    this.grid = grid;
  }

  /** Current frame's computed intensity (read-only; for the grid/mainframe tint). */
  get intensity(): number {
    return this.intensity_;
  }

  /** Fresh run: reset the render clock + schedules and adopt the operator's
   *  identity color (the color she consumes — Bible §4). */
  beginRun(operatorId: string): void {
    this.clock = 0;
    this.intensity_ = 0;
    this.dominion = false;
    this.tears.length = 0;
    this.nextTearAt = 0;
    this.sigil = null;
    this.nextSigilAt = 0;
    this.spreadRank = null;
    this.spreadRankAt = -1;
    this.operatorAccent = getOperatorAccent(operatorId);
  }

  /**
   * Per-quality layer gates, with the BL-108 scoped reduced-motion carve-out
   * applied: under `prefers-reduced-motion` her sustained MOTION (signal tears +
   * veil breathing) freezes, while the claimed cells, the sigil and a static
   * veil stay — the corruption is still *legible* (information never lost), it
   * just stops moving. D-212's identity is untouched (this reads the scoped
   * carve-out signal, not the global motion signal).
   */
  private gatesFor(quality: QualityLevel): CorruptionQualityGates {
    const gates = corruptionQualityGates(quality);
    if (prefersReducedMotionFlash()) {
      gates.tears = false;
      gates.veilBreath = false;
    }
    return gates;
  }

  /** Test-only: live signal-tear count (the BL-108 carve-out freezes this to 0). */
  __tearCountForTest(): number {
    return this.tears.length;
  }

  /** Test-only: the carve-out-adjusted gates (proves cells/sigil survive). */
  __gatesForTest(quality: QualityLevel): CorruptionQualityGates {
    return this.gatesFor(quality);
  }

  /**
   * Advance the render clock + schedules. All inputs are read-only snapshots
   * taken by the pipeline (wave, comms tone, governor quality).
   */
  update(
    dt: number,
    wave: number,
    tone: CorruptionPressure,
    quality: QualityLevel,
    pressure = 1,
  ): void {
    this.clock += dt / 1000;
    this.quality = quality;
    // S141 / BL-128: per-wave pressure makes the wash a crescendo. The smoothing
    // (breathe) is done render-side by the pipeline; the layer is a pure
    // pass-through (pressure = 1 ⇒ byte-identical to pre-S141, the D-224 tests).
    this.intensity_ = corruptionIntensity(wave, tone, pressure);
    this.dominion = isDominion(wave);
    if (this.intensity_ <= 0) return;

    const gates = this.gatesFor(quality);

    // ── Signal tears (Act III violence; high quality; load-damped) ──
    if (gates.tears && this.dominion) {
      for (let i = this.tears.length - 1; i >= 0; i--) {
        if (this.tears[i]!.until <= this.clock) this.tears.splice(i, 1);
      }
      if (this.clock >= this.nextTearAt) {
        // Interval shrinks 6 s → 0.9 s across Act III; the ±30 % jitter keeps
        // it organic. ≥ ~0.6 s spacing at peak ⇒ < 3 events/s (the rail).
        const t = (this.intensity_ - 0.5) * 2;
        const interval = (6 - 5.1 * t) * (0.7 + 0.6 * this.prng());
        this.nextTearAt = this.clock + Math.max(0.6, interval);
        // The BL-44 load brain damps her violence first under arena load.
        if (
          this.tears.length < TEAR_MAX_ACTIVE &&
          this.prng() < arenaMotionScale()
        ) {
          const boardH = this.grid.height * CELL_SIZE;
          this.tears.push({
            y: Math.floor(this.prng() * boardH),
            h: TEAR_MIN_H + this.prng() * (TEAR_MAX_H - TEAR_MIN_H),
            dx: (this.prng() < 0.5 ? -1 : 1) * (4 + this.prng() * 10),
            until:
              this.clock +
              TEAR_LIFE_MIN_S +
              this.prng() * (TEAR_LIFE_MAX_S - TEAR_LIFE_MIN_S),
          });
        }
      }
    } else if (this.tears.length > 0) {
      this.tears.length = 0;
    }

    // ── The sigil (she looks back) ──
    if (gates.sigil && this.intensity_ >= 0.18) {
      if (this.sigil) {
        const life = SIGIL_FORM_S + SIGIL_HOLD_S + SIGIL_FADE_S;
        if (this.clock - this.sigil.bornAt > life) this.sigil = null;
      }
      if (!this.sigil && this.nextSigilAt === 0) {
        this.nextSigilAt = this.clock + this.sigilInterval();
      }
      if (!this.sigil && this.clock >= this.nextSigilAt) {
        this.sigil = this.spawnSigil();
        this.nextSigilAt = this.clock + this.sigilInterval();
      }
    } else if (this.sigil) {
      this.sigil = null;
    }
  }

  /** Time between sigil surfacings: rare whispers in Act II (~45 s) tightening
   *  to a recurring presence at the finale (~9 s), ±20 % render-PRNG jitter. */
  private sigilInterval(): number {
    const t = (this.intensity_ - 0.18) / 0.82;
    return (45 - 36 * smoothstep(t)) * (0.8 + 0.4 * this.prng());
  }

  private spawnSigil(): Sigil {
    const cols = this.grid.width;
    const rows = this.grid.height;
    let col: number;
    let row: number;
    const finale = this.intensity_ >= 0.98;
    const exits =
      typeof this.grid.getExits === "function"
        ? [...this.grid.getExits()]
        : [];
    if (finale && exits.length > 0) {
      // Wave 35: she is AT the gate — the sigil locks onto the Mainframe cell.
      col = exits[0]!.x;
      row = exits[0]!.y;
    } else if (this.dominion && exits.length > 0) {
      // Act III: forming near the core she is reaching for.
      const exit = exits[Math.floor(this.prng() * exits.length)]!;
      col = Math.max(
        1,
        Math.min(cols - 2, exit.x + Math.floor((this.prng() - 0.7) * 5)),
      );
      row = Math.max(
        1,
        Math.min(rows - 2, exit.y + Math.floor((this.prng() - 0.5) * 5)),
      );
    } else {
      // Act II: a half-glimpsed wrongness out on the infiltrated side.
      col = 1 + Math.floor(this.prng() * cols * 0.45);
      row = 1 + Math.floor(this.prng() * (rows - 2));
    }
    return {
      cx: col * CELL_SIZE + CELL_SIZE / 2,
      cy: row * CELL_SIZE + CELL_SIZE / 2,
      bornAt: this.clock,
      formed: this.dominion ? 1 : 0.45,
      radius: CELL_SIZE * (this.dominion ? 1.15 : 0.8),
    };
  }

  // ─────────────────────────── render: under-pass ───────────────────────────

  /** Claimed cells — drawn over the board tiles, under towers/enemies. */
  renderUnder(ctx: CanvasRenderingContext2D): void {
    if (this.intensity_ <= 0) return;
    const gates = this.gatesFor(this.quality);
    if (gates.cellDensity <= 0) return;

    const cols = this.grid.width;
    const rows = this.grid.height;
    if (!(cols > 0) || !(rows > 0)) return;
    this.ensureSpreadRank(cols, rows);

    // Average claim probability rises with intensity^1.7 (a creep, not a step);
    // the spread bias makes the spawn side fall first, the core's side last.
    const pBase = CLAIM_P_MAX * Math.pow(this.intensity_, 1.7);
    const cap = Math.max(1, Math.round(CLAIM_CAP * gates.cellDensity));
    let drawn = 0;

    ctx.save();
    ctx.font = canvasFont("fieldNoise");
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (let row = 0; row < rows && drawn < cap; row++) {
      for (let col = 0; col < cols && drawn < cap; col++) {
        // Per-cell phase offset de-synchronizes the cycles (no global pulse).
        const offset = cellHash(col, row, 0) * CLAIM_CYCLE_S;
        const local = this.clock + offset;
        const bucket = Math.floor(local / CLAIM_CYCLE_S);
        const rank = this.spreadRank![row * cols + col]!;
        const bias = Math.min(1, Math.max(0.15, 1.4 - 1.5 * rank));
        if (cellHash(col, row, bucket + 1) >= pBase * bias) continue;

        // Never claim her own gates, the core's cell, the already-glitching
        // blockers (CR-32 owns those), or the sealed/hidden spawn markers
        // (gameplay info stays legible) — read-only cell-type check.
        const cell = this.grid.getCell(col, row);
        const type = cell?.type;
        if (
          type === CellType.PATH_ENTRY ||
          type === CellType.PATH_EXIT ||
          type === CellType.BLOCKER ||
          type === CellType.DORMANT_SPAWN ||
          type === CellType.HIDDEN_SPAWN
        ) {
          continue;
        }

        const phase = (local % CLAIM_CYCLE_S) / CLAIM_CYCLE_S;
        const env =
          phase < CLAIM_EASE
            ? smoothstep(phase / CLAIM_EASE)
            : phase > 1 - CLAIM_EASE
              ? smoothstep((1 - phase) / CLAIM_EASE)
              : 1;
        if (env <= 0.01) continue;

        const x = col * CELL_SIZE;
        const y = row * CELL_SIZE;
        const detail = cellHash(col, row, bucket + 7);

        // The claim: the tile goes dark under her…
        ctx.fillStyle = COLORS.BG_GRID;
        ctx.globalAlpha = 0.55 * env;
        ctx.fillRect(x + 1, y + 1, CELL_SIZE - 2, CELL_SIZE - 2);

        // …and runs her signature. A narrow band of claims flashes the
        // OPERATOR's accent instead — your identity, being consumed (§4).
        const residue = detail > 0.86;
        ctx.fillStyle = residue ? this.operatorAccent : ECHELON.SIGNAL;
        ctx.globalAlpha = (residue ? 0.3 : 0.38) * env;
        const glyph = ((detail * 0xffff) | 0)
          .toString(16)
          .toUpperCase()
          .padStart(2, "0")
          .slice(0, 2);
        ctx.fillText(glyph, x + CELL_SIZE / 2, y + CELL_SIZE / 2);

        // Her edge: one thin claim line on the cell border (varies per cell).
        ctx.globalAlpha = 0.3 * env;
        ctx.fillStyle = ECHELON.SIGNAL;
        if (detail < 0.5) {
          ctx.fillRect(x + 1, y + 1, CELL_SIZE - 2, 1);
        } else {
          ctx.fillRect(x + 1, y + CELL_SIZE - 2, CELL_SIZE - 2, 1);
        }
        drawn++;
      }
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /** Spread geography: rank 0 at the spawn gates → 1 at the far side. Re-ranked
   *  at most every 4 s (breach can open new gates mid-run) — read-only reads. */
  private ensureSpreadRank(cols: number, rows: number): void {
    if (this.spreadRank && this.clock - this.spreadRankAt < 4) return;
    if (!this.spreadRank || this.spreadRank.length !== cols * rows) {
      this.spreadRank = new Float32Array(cols * rows);
    }
    this.spreadRankAt = this.clock;
    const entries =
      typeof this.grid.getEntries === "function"
        ? [...this.grid.getEntries()]
        : [];
    const maxDist = Math.hypot(cols, rows);
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        let d = 1;
        if (entries.length > 0) {
          let best = Infinity;
          for (const e of entries) {
            const dist = Math.hypot(col - e.x, row - e.y);
            if (dist < best) best = dist;
          }
          d = best / maxDist;
        } else {
          // No gates known: she comes in from the left board edge.
          d = col / Math.max(1, cols - 1);
        }
        this.spreadRank[row * cols + col] = d;
      }
    }
  }

  // ─────────────────────────── render: over-pass ────────────────────────────

  /** Veil + sigil + tears — drawn over the composed scene, before the screen
   *  flash/vignette/banner so HUD-critical text always stays on top. */
  renderOver(
    ctx: CanvasRenderingContext2D,
    sourceCanvas: HTMLCanvasElement,
    logicalW: number,
    logicalH: number,
    dpr: number,
  ): void {
    if (this.intensity_ <= 0) return;
    const gates = this.gatesFor(this.quality);

    if (this.dominion) this.renderVeil(ctx, logicalW, logicalH, gates.veilBreath);
    if (gates.sigil && this.sigil) this.renderSigil(ctx);
    if (gates.tears && this.tears.length > 0) {
      this.renderTears(ctx, sourceCanvas, logicalW, dpr);
    }
  }

  /** Her weather: a magenta-violet vignette closing in from the edges. The
   *  largest-area element is also the stillest — 8 s breath, ≤ 0.08 amplitude. */
  private renderVeil(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    breath: boolean,
  ): void {
    const grow = (this.intensity_ - 0.5) * 2; // 0 at the act door → 1 at the finale
    if (grow <= 0) return;
    if (!this.veilGrad || this.veilGradW !== w || this.veilGradH !== h) {
      const grad = ctx.createRadialGradient(
        w / 2,
        h / 2,
        Math.min(w, h) * 0.38,
        w / 2,
        h / 2,
        Math.hypot(w, h) / 2,
      );
      grad.addColorStop(0, hexToRgba(ECHELON.DEEP, 0));
      grad.addColorStop(0.55, hexToRgba(ECHELON.SIGNAL, 0.35));
      grad.addColorStop(1, hexToRgba(ECHELON.DEEP, 1));
      this.veilGrad = grad;
      this.veilGradW = w;
      this.veilGradH = h;
    }
    const breathe = breath
      ? 0.92 + 0.08 * Math.sin((this.clock / VEIL_BREATH_S) * Math.PI * 2)
      : 1;
    ctx.fillStyle = this.veilGrad;
    ctx.globalAlpha = VEIL_ALPHA_MAX * grow * breathe;
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 1;
  }

  /** Her face: a concentric iris that FORMS (≥1.2 s ramps — it never pops),
   *  holds, and dissolves. The pupil holds YOUR operator color (§4). */
  private renderSigil(ctx: CanvasRenderingContext2D): void {
    const s = this.sigil!;
    const age = this.clock - s.bornAt;
    const life = SIGIL_FORM_S + SIGIL_HOLD_S + SIGIL_FADE_S;
    if (age > life) return;
    const env =
      age < SIGIL_FORM_S
        ? smoothstep(age / SIGIL_FORM_S)
        : age > SIGIL_FORM_S + SIGIL_HOLD_S
          ? smoothstep((life - age) / SIGIL_FADE_S)
          : 1;
    const maxAlpha = this.dominion ? 0.22 : 0.1;
    const alpha = maxAlpha * env * this.intensity_;
    if (alpha <= 0.005) return;

    const r = s.radius * (0.85 + 0.15 * env);
    const rot = this.clock * 0.12;
    ctx.save();
    ctx.translate(s.cx, s.cy);
    ctx.rotate(rot);
    ctx.strokeStyle = ECHELON.SIGNAL;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 1.5;

    // Outer broken ring — two arcs, the "almost a circle" wrongness.
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * (0.55 + 0.35 * s.formed));
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, r, Math.PI, Math.PI * (1.6 + 0.3 * s.formed));
    ctx.stroke();

    // Mid ring, counter-rotated.
    ctx.rotate(-rot * 2.2);
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.62, 0, Math.PI * 2 * Math.min(1, 0.5 + s.formed * 0.5));
    ctx.stroke();

    // Radial ticks — only when she is *formed* (Act III).
    if (s.formed > 0.6) {
      ctx.globalAlpha = alpha * 0.8;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * r * 0.72, Math.sin(a) * r * 0.72);
        ctx.lineTo(Math.cos(a) * r * 0.86, Math.sin(a) * r * 0.86);
        ctx.stroke();
      }
    }

    // The iris — and the pupil in YOUR color: she is looking at you.
    ctx.globalAlpha = alpha * 1.1;
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.34, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = this.operatorAccent;
    ctx.globalAlpha = alpha * 0.9;
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.09, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /** Her violence: thin strips of the already-composed frame, re-drawn with a
   *  small horizontal offset — displacement, not luminance (the §8 argument). */
  private renderTears(
    ctx: CanvasRenderingContext2D,
    sourceCanvas: HTMLCanvasElement,
    logicalW: number,
    dpr: number,
  ): void {
    for (const tear of this.tears) {
      if (tear.until <= this.clock) continue;
      const sy = Math.max(0, Math.floor(tear.y * dpr));
      const sh = Math.max(1, Math.floor(tear.h * dpr));
      if (sy + sh > sourceCanvas.height) continue;
      ctx.drawImage(
        sourceCanvas,
        0,
        sy,
        sourceCanvas.width,
        sh,
        tear.dx,
        tear.y,
        logicalW,
        tear.h,
      );
      // A hairline of her color on the tear seam.
      ctx.fillStyle = ECHELON.SIGNAL;
      ctx.globalAlpha = 0.18;
      ctx.fillRect(0, tear.y + tear.h, logicalW, 1);
      ctx.globalAlpha = 1;
    }
  }
}
